import React, { useState } from 'react';
import { StepName } from '|/app/services';
import LinkButton from '|/components/carvana-ds/link-button';
import useOrderSummaryVm from '|/components/carvana-ds/order-summary-display/use-order-summary-vm';
import currency from '|/helpers/currency';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { AmountRow, ExpanderRow } from './amount-row';
import { PriceDetailsView } from '../derive-price-details';
import styles from '../price-details.module.css';

interface HowYourePayingSectionProps {
  summary: ReviewSummaryResponse;
  view: PriceDetailsView;
}

const HowYourePayingSection = ({ summary, view }: HowYourePayingSectionProps) => {
  const [isDueOpen, setIsDueOpen] = useState(false);
  const navigateToStep = useNavigateToStep();
  const { dueLabelTotalSummary, dueAmount, cobuyerName, hasCoBuyerName, isCoApplicantToggleOn } =
    useOrderSummaryVm(summary);
  const { dueBreakdown, lenderName, financedWithLender } = view;

  return (
    <section className={styles.majorSection} data-testid="pp_price-details_how-youre-paying">
      <div className={styles.majorHeading}>
        <h3 className={styles.majorTitle}>How you&apos;re paying</h3>
        <LinkButton
          size="xs"
          underline={false}
          onClick={() => {
            navigateToStep(StepName.CoF);
          }}
          data-analytics-id="pp_review_order_edit_payment_type"
          data-testid="pp_price-details_edit-details"
        >
          Edit details
        </LinkButton>
      </div>

      <div className={styles.paymentType}>
        <p className={styles.paymentTypeLabel}>Payment type</p>
        <p className={styles.paymentTypeValue}>{view.paymentTypeLabel}</p>
      </div>

      {isCoApplicantToggleOn && hasCoBuyerName && (
        <div className={styles.tradeGroup} data-testid="pp_price-details_cosigner">
          <div className={styles.paymentType}>
            <p className={styles.paymentTypeLabel}>Co-signer</p>
            <p className={styles.paymentTypeValue}>{cobuyerName}</p>
          </div>
          <p className={styles.paymentNote}>{view.cosignerNote}</p>
        </div>
      )}

      <div className={styles.sectionBody}>
        {view.monthlyPayment !== null && (
          <div className={styles.tradeGroup}>
            <AmountRow
              label="Monthly payment"
              amount={`${currency(view.monthlyPayment, { minimumFractionDigits: 0 })}/mo`}
              emphasis="total"
            />
            {view.loanTermsCopy && <p className={styles.paymentNote}>{view.loanTermsCopy}</p>}
          </div>
        )}

        {lenderName && (
          <div className={styles.paymentType} data-testid="pp_price-details_lender">
            <p className={styles.paymentTypeLabel}>Lender</p>
            <p className={styles.paymentNote}>{lenderName}</p>
          </div>
        )}

        {financedWithLender !== null && (
          <AmountRow
            label="Financed with lender"
            amount={currency(financedWithLender)}
            testId="pp_price-details_financed-with-lender"
          />
        )}

        {dueBreakdown.length > 0 ? (
          <ExpanderRow
            label={dueLabelTotalSummary}
            amount={currency(dueAmount)}
            isOpen={isDueOpen}
            onToggle={() => {
              setIsDueOpen(!isDueOpen);
            }}
            analyticsId="pp_price_details_total_due_toggle"
            testId="pp_price-details_total-due-toggle"
            strongLabel
          >
            {dueBreakdown.map(item => (
              <AmountRow
                key={item.id}
                label={item.title}
                amount={`${item.id === 'refund' ? '-' : ''}${currency(item.amount)}`}
                emphasis="weak"
              />
            ))}
          </ExpanderRow>
        ) : (
          <AmountRow label={dueLabelTotalSummary} amount={currency(dueAmount)} emphasis="total" />
        )}
      </div>
    </section>
  );
};

export default HowYourePayingSection;
